import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';

// Privacy Checkup for the web dashboard: the same guided checklist as the
// iPhone app. No scanning; it points to Apple's own privacy controls.

const STORAGE_KEY = 'cg_privacy_checklist_done';

const STEPS = [
  { id: 'safety_check', title: "Run Apple's Safety Check", why: 'Shows who you share location, photos and other information with, and lets you stop sharing in one place.', path: 'Settings → Privacy & Security → Safety Check' },
  { id: 'camera', title: 'Review camera access', why: "Turn off camera access for any app you don't recognize or no longer use.", path: 'Settings → Privacy & Security → Camera' },
  { id: 'microphone', title: 'Review microphone access', why: "Remove microphone access you didn't intend to give.", path: 'Settings → Privacy & Security → Microphone' },
  { id: 'location', title: 'Review location access', why: 'Check which apps can see your location, and whether it\'s "Always" or "While Using".', path: 'Settings → Privacy & Security → Location Services' },
  { id: 'find_my', title: 'Check who sees your location', why: 'Make sure you recognize everyone you share your location with.', path: 'Find My app → People' },
  { id: 'devices', title: 'Check devices on your Apple Account', why: "Remove any device you don't recognize, then change your password.", path: 'Settings → [your name] → your devices' },
  { id: 'profiles', title: 'Look for unknown device profiles', why: "Remove any profile you didn't install (unless it's from your school or employer).", path: 'Settings → General → VPN & Device Management' },
  { id: 'stolen_device', title: 'Turn on Stolen Device Protection', why: 'Adds Face ID or Touch ID and a delay before sensitive changes away from familiar places.', path: 'Settings → Face ID & Passcode → Stolen Device Protection' },
  { id: 'passcode', title: 'Use a passcode only you know', why: 'If someone else knows your passcode, change it.', path: 'Settings → Face ID & Passcode → Change Passcode' },
  { id: 'updates', title: 'Keep iOS up to date', why: 'Turn on automatic updates so security fixes install right away.', path: 'Settings → General → Software Update' },
];

const cardStyle = { background: 'rgba(255, 255, 255, 0.05)', border: '1px solid rgba(0, 212, 255, 0.2)', borderRadius: '1.5rem' };

const PrivacyCheckupWeb = () => {
  const [done, setDone] = useState({});

  useEffect(() => {
    try { setDone(JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}')); } catch (e) { /* progress is optional */ }
  }, []);

  const toggle = (id) => {
    setDone(prev => {
      const next = { ...prev, [id]: !prev[id] };
      try { localStorage.setItem(STORAGE_KEY, JSON.stringify(next)); } catch (e) { /* ignore */ }
      return next;
    });
  };

  const count = STEPS.filter(s => done[s.id]).length;

  return (
    <div className="space-y-6">
      <Card style={cardStyle}>
        <CardHeader>
          <CardTitle style={{ color: '#ffffff' }}>Privacy Checkup</CardTitle>
          <CardDescription style={{ color: '#cbd5e1' }}>
            {count} of {STEPS.length} steps reviewed. Work through Apple's built-in privacy controls on your iPhone. iOS doesn't let any app scan your iPhone for spyware, so these steps use Apple's own tools.
          </CardDescription>
        </CardHeader>
      </Card>
      {STEPS.map(step => (
        <Card key={step.id} style={cardStyle}>
          <CardContent className="pt-6 flex items-start gap-4">
            <input type="checkbox" className="mt-1 h-5 w-5" checked={!!done[step.id]} onChange={() => toggle(step.id)} aria-label={`Mark "${step.title}" as reviewed`} />
            <div>
              <p className="font-semibold text-white">{step.title}</p>
              <p className="text-sm" style={{ color: '#cbd5e1' }}>{step.why}</p>
              <p className="text-sm font-semibold" style={{ color: '#67e8f9' }}>{step.path}</p>
            </div>
          </CardContent>
        </Card>
      ))}
      <Card style={cardStyle}>
        <CardContent className="pt-6" style={{ color: '#cbd5e1' }}>
          Worried someone has access to your phone? Apple's{' '}
          <a href="https://support.apple.com/guide/personal-safety/welcome/web" target="_blank" rel="noopener noreferrer" className="underline" style={{ color: '#67e8f9' }}>Personal Safety guide</a>{' '}
          explains how to check and remove access. If you're in danger, contact local emergency services.
        </CardContent>
      </Card>
    </div>
  );
};

export default PrivacyCheckupWeb;
